import type { ModalProps } from '@workramp/ui';
import type { ReactNode, RefObject } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, TextArea, TextField } from '@workramp/ui';
import { useRef, useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import { StyledCharacterLimit } from './PromptModal.styles';

export type PromptModalProps = Omit<ModalProps, 'children' | 'isOpen'> & {
  isOpen: boolean;
  title: string;
  fieldLabel?: string;
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  confirm?: string;
  autoSelect?: boolean;
  fieldType?: 'text' | 'textarea';
  isRequired?: boolean;
  characterLimit?: number;
  secondaryActions?: ReactNode;
  onChange?: (value: string) => void;
  onClose?: () => void;
  onConfirm: (name: string) => void;
};

export function PromptModal({
  title,
  fieldLabel,
  value = '',
  defaultValue = '',
  confirm,
  autoSelect = false,
  placeholder = '',
  fieldType = 'text',
  characterLimit,
  isRequired = false,
  secondaryActions,
  onChange,
  onClose,
  onConfirm,
  ...modalProps
}: PromptModalProps) {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [editableValue, setEditableValue] = useState(defaultValue);

  const inputValue = editableValue || value;
  const handleChange = onChange || setEditableValue;

  const handleConfirm = () => onConfirm(inputValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Modal
      onClose={onClose}
      onEnter={() => setEditableValue(defaultValue || '')}
      onEntered={() => {
        if (autoSelect) {
          ref.current?.setSelectionRange(0, ref.current.value.length);
        }
      }}
      {...modalProps}
    >
      <ModalHeader title={title} />
      <ModalBody>
        {fieldType === 'text' && (
          <TextField
            ref={ref as RefObject<HTMLInputElement>}
            label={fieldLabel || title}
            hideLabel={!fieldLabel}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={characterLimit}
            autoFocus
          />
        )}
        {fieldType === 'textarea' && (
          <TextArea
            ref={ref as RefObject<HTMLTextAreaElement>}
            label={title}
            hideLabel
            value={inputValue}
            onChange={handleChange}
            placeholder={placeholder}
            maxLength={characterLimit}
            autoFocus
          />
        )}
        {typeof characterLimit === 'number' && (
          <StyledCharacterLimit size={0}>
            {I18n.t('js.admin.guide.description_character_limit', {
              count: inputValue.length,
              max: characterLimit,
            })}
          </StyledCharacterLimit>
        )}
      </ModalBody>
      <ModalFooter>
        <Row align="center" gapX={2}>
          <Col>{secondaryActions}</Col>
          <Col xs="auto">
            <Button size="lg" color="secondary" variant="text" onPress={onClose}>
              {I18n.t('js.common.modal.cancel')}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              size="lg"
              color="primary"
              onPress={handleConfirm}
              isDisabled={isRequired && !value && !editableValue}
              data-testid="confirm-button"
            >
              {confirm || I18n.t('js.common.modal.save')}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
}
